/**
 * Shared job lifecycle for Services + Proxy tablet hubs.
 * Informational layer only — settlement stays client-side / escrow rails.
 */

export const JOB_STATUSES = [
  'draft',
  'matching',
  'open',
  'quoted',
  'accepted',
  'scheduled',
  'in_progress',
  'complete',
  'settled',
  'disputed',
  'cancelled',
];

export const JOB_STATUS_LABELS = {
  draft: 'Draft',
  matching: 'Matching — claim window open',
  open: 'Open — waiting for quotes',
  quoted: 'Quotes received',
  accepted: 'Quote accepted',
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  complete: 'Complete',
  settled: 'Settled',
  disputed: 'Disputed',
  cancelled: 'Cancelled',
};

/** Allowed next statuses from current (demo / tablet transitions). */
export const JOB_TRANSITIONS = {
  draft: ['matching', 'open', 'cancelled'],
  matching: ['quoted', 'open', 'accepted', 'cancelled'],
  open: ['matching', 'quoted', 'cancelled'],
  quoted: ['accepted', 'matching', 'open', 'cancelled', 'disputed'],
  accepted: ['scheduled', 'in_progress', 'cancelled', 'disputed'],
  scheduled: ['in_progress', 'cancelled', 'disputed'],
  in_progress: ['complete', 'disputed', 'cancelled'],
  complete: ['settled', 'disputed'],
  settled: [],
  disputed: ['settled', 'cancelled'],
  cancelled: [],
};

export const canTransition = (from, to) => (JOB_TRANSITIONS[from] || []).includes(to);

/** Non-custodial escrow milestone states (demo / informational UI). */
export const ESCROW_STATUSES = ['none', 'offered', 'locked', 'released', 'refunded'];

export const ESCROW_STATUS_LABELS = {
  none: 'No escrow',
  offered: 'Escrow offered — lock when ready',
  locked: 'Funds locked (non-custodial)',
  released: 'Released to provider',
  refunded: 'Refunded to payer',
};

export const ESCROW_MILESTONE_OPTIONS = [
  { value: 'full', label: 'Full amount on accept' },
  { value: 'deposit', label: 'Deposit now · balance on complete' },
  { value: 'on_complete', label: 'Lock only when work completes' },
];

export const generateHandoffPin = () =>
  String(Math.floor(100000 + Math.random() * 900000));

export const MATCH_WINDOW_MS = 45 * 1000;

export const MATCH_MODE_OPTIONS = [
  { value: 'best_quote', label: 'Best quote within window' },
  { value: 'first_claim', label: 'First claim wins' },
];

export const CANCEL_REASON_OPTIONS = [
  { value: 'changed_mind', label: 'Changed my mind' },
  { value: 'found_elsewhere', label: 'Found someone else' },
  { value: 'price', label: 'Price too high' },
  { value: 'schedule', label: 'Schedule no longer works' },
  { value: 'no_quotes', label: 'No suitable quotes' },
  { value: 'other', label: 'Other' },
];

export const NO_SHOW_REASON_OPTIONS = [
  { value: 'provider_no_show', label: 'Provider / runner didn’t show' },
  { value: 'buyer_no_show', label: 'I couldn’t make it (buyer)' },
  { value: 'wrong_address', label: 'Wrong / unreachable location' },
  { value: 'access_blocked', label: 'Access blocked on site' },
];

export const DISPUTE_REASON_OPTIONS = [
  { value: 'quality', label: 'Work quality / incomplete' },
  { value: 'damage', label: 'Damage or loss' },
  { value: 'wrong_item', label: 'Wrong item / scope mismatch' },
  { value: 'payment', label: 'Payment / amount dispute' },
  { value: 'no_show', label: 'No-show' },
  { value: 'other', label: 'Other' },
];

export const scheduleWindowOptions = [
  { value: 'asap', label: 'ASAP / today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'this_week', label: 'This week' },
  { value: 'weekend', label: 'This weekend' },
  { value: 'next_week', label: 'Next week' },
  { value: 'flexible', label: 'Flexible — I’ll confirm' },
  { value: 'custom', label: 'Specific date (say in notes)' },
];

export const newEntityId = prefix =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? `${prefix}-${crypto.randomUUID()}`
    : `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Eligible providers for a brief (category + optional online/on-site filter).
 */
export const filterEligibleProviders = (providers, brief = {}) => {
  const categoryId = brief.categoryId || brief.becomeCategory;
  const deliveryMode = brief.deliveryMode;
  return (providers || []).filter(p => {
    if (categoryId && categoryId !== 'all' && p.categoryId !== categoryId && p.categoryId !== 'all') {
      return false;
    }
    if (deliveryMode === 'remote_online' && p.deliveryModes?.length) {
      return p.deliveryModes.includes('remote_online');
    }
    if (deliveryMode === 'on_site' && p.deliveryModes?.length) {
      return p.deliveryModes.includes('on_site') || p.deliveryModes.includes('shop');
    }
    return true;
  });
};

/**
 * Build demo quotes from providers matching the job category.
 */
export const buildDemoQuotes = (providers, { categoryId, budget, currency, brief } = {}) => {
  const pool = filterEligibleProviders(providers, brief || { categoryId });
  const picks = (pool.length ? pool : providers || []).slice(0, 3);
  const base = parseFloat(String(budget || '').replace(/[^0-9.]/g, '')) || 5000;
  const cur = currency || 'JMD';

  return picks.map((p, i) => {
    const factor = 0.85 + i * 0.18 + ((p.id?.length || 1) % 5) * 0.02;
    const amount = Math.round(base * factor);
    return {
      id: newEntityId('quote'),
      providerId: p.id,
      providerName: p.name,
      amount: String(amount),
      currency: cur,
      etaWindow: p.etaHint || ['Today 2–5 pm', 'Tomorrow morning', 'Within 48h'][i % 3],
      note: p.quoteNote || p.blurb || 'Happy to take this on.',
      createdAt: new Date().toISOString(),
      claimable: true,
    };
  });
};

/** Stagger quote arrivals for smart-match demo (one provider per tick). */
export const buildStaggeredQuotePlan = (providers, brief = {}) => {
  const eligible = filterEligibleProviders(providers, brief);
  const picks = (eligible.length ? eligible : providers || []).slice(0, 4);
  return picks.map((p, i) => ({
    delayMs: 4000 + i * 7000,
    provider: p,
  }));
};

export default {
  JOB_STATUSES,
  JOB_STATUS_LABELS,
  canTransition,
  MATCH_WINDOW_MS,
};
